import { useState, useContext } from 'react';
import ConfirmDeletionModal from './ConfirmDeletionModal';
import AuthContext from '../../../contexts/auth/AuthContext';
import ProcessContext from '../../../contexts/process/ProcessContext';
import TenantsApiService from '../../../services/apiServices/TenantsApiService';
import { TENANT_ADMIN, OPERATOR, MARKETING } from '../../../constants/roles/roles';
import Button from '../../layout/Button';

const Card = ({tenantUser, handleDelete}) => {
    const [editEnabled, setEditEnabled] = useState(false);
    const [showConfirmDeletionModal, setShowConfirmDeletionModal] = useState(false);
    const { currentTenant, userInformation, isGlobalAdmin} = useContext(AuthContext);
    const { notifySuccess, notifyError } = useContext(ProcessContext);
    const normalize = (s) => (s ?? '').trim().toLowerCase();
    const isSameUser = normalize(tenantUser?.email) === normalize(userInformation?.email);

    let initialUserState = {
        id: tenantUser.id,
        familyName: tenantUser.familyName,
        givenName: tenantUser.givenName,
        email: tenantUser.email,
        roles: tenantUser.roles
    };

    const [user, setUser] = useState(initialUserState);
    const [userCancel, setUserCancel] = useState(initialUserState);

    const changeRoles = (role) => {
        const tenantAdminWasSaved = userCancel.roles?.includes(TENANT_ADMIN);
        const isRemovingTenantAdmin = role === TENANT_ADMIN && user.roles?.includes(TENANT_ADMIN);

        if (role === TENANT_ADMIN && isSameUser && tenantAdminWasSaved && isRemovingTenantAdmin && !isGlobalAdmin) {
                 return;
               }
        if (user.roles.includes(role)) {
            let newRoles = user.roles.filter(e => e !== role)
            setUser({
                ...user,
                roles: newRoles
            });

            return;
        }

        let newRoles = [...user.roles, role];
        setUser({
            ...user,
            roles: newRoles
        });
    };

    const toggleEdit = () => {
        if (editEnabled) {
            setUser(userCancel);
        }
        setEditEnabled(!editEnabled);
    };

    const onCancelConfirmDeletion = () => {
        setShowConfirmDeletionModal(false);
    };

    const confirmDeletion = () => {
        if (isSameUser)
        {
            addNotification("You cannot delete your own account.", "warning");
            return;
        }
        handleDelete(user.id);
    };

    const updateUserRoles = () => {
        TenantsApiService.updateUserRolesForTenant(currentTenant.id, user.id, user.roles).then(() => {
            notifySuccess("Successfully updated user roles.");
            setUserCancel({
                ...userCancel,
                roles: user.roles
            });
        })
            .catch((e) => {
                if (e?.statusCode === 500) {
                    notifyError("We have encountered an issue on our side while updating a produce type. Please contact support team support.farmer@diecityfarm.de.");
                }
                else if (e?.statusCode === 404) {
                    notifyError("We have encountered an issue on our side while updating user roles. Please log out, log back in and try again. If this error persists please contact support team support.farmer@diecityfarm.de.");
                }
                else {
                    notifyError("We have encountered an issue on our side while updating user roles. Please contact support team support.farmer@diecityfarm.de. No detailed error information possible.");
                }
                setUser(userCancel);
                console.log("Error while updating user roles: ", e);
            })
            .finally(() => {
                setEditEnabled(!editEnabled);
            });
    };

    return <>
        <ConfirmDeletionModal show={showConfirmDeletionModal} email={user.email} onCancel={onCancelConfirmDeletion} onConfirm={confirmDeletion} />
        <div className="tenant-user-management-card">
            <div className="tenant-user-management-card-header">
                <div className="tenant-user-management-card-name">
                    {user.familyName && user.givenName ? user.familyName + ", " + user.givenName : 'No name provided yet'}
                </div>
            </div>
            <div className="tenant-user-management-card-email tenant-user-management-card-label">
                Email
            </div>
            <div className="tenant-user-management-card-email tenant-user-management-card-value">
                {user.email}
            </div>
            <div className="tenant-user-management-card-roles tenant-user-management-card-label">
                Roles
            </div>
            {!editEnabled ?
                <div>
                    <input type="checkbox" checked={user.roles?.includes(OPERATOR)} disabled className="tenant-user-management-card-operator tenant-user-management-card-checkbox-value-disabled"></input>
                    <div className="tenant-user-management-card-operator tenant-user-management-card-checkbox-label tenant-user-management-card-checkbox-label-readonly">
                        Operator
                    </div>
                    <input type="checkbox" checked={user.roles?.includes(MARKETING)} disabled className="tenant-user-management-card-marketing tenant-user-management-card-checkbox-value-disabled"></input>
                    <div className="tenant-user-management-card-marketing tenant-user-management-card-checkbox-label tenant-user-management-card-checkbox-label-readonly">
                        Marketing
                    </div>
                    <input type="checkbox" checked={user.roles?.includes(TENANT_ADMIN)} disabled className="tenant-user-management-card-tenant-admin tenant-user-management-card-checkbox-value-disabled"></input>
                    <div className="tenant-user-management-card-tenant-admin tenant-user-management-card-checkbox-label tenant-user-management-card-checkbox-label-readonly">
                        Tenant Admin
                    </div>
                </div>
                :
                <div>
                    <input type="checkbox" onChange={() => changeRoles(OPERATOR)} checked={user.roles?.includes(OPERATOR)} className="tenant-user-management-card-operator tenant-user-management-card-checkbox-value-enabled"></input>
                    <div className="tenant-user-management-card-operator tenant-user-management-card-checkbox-label" onClick={() => changeRoles(OPERATOR)}>
                        Operator
                    </div>
                    <input type="checkbox" onChange={() => changeRoles(MARKETING)} checked={user.roles?.includes(MARKETING)} className="tenant-user-management-card-marketing tenant-user-management-card-checkbox-value-enabled"></input>
                    <div className="tenant-user-management-card-marketing tenant-user-management-card-checkbox-label" onClick={() => changeRoles(MARKETING)}>
                        Marketing
                    </div>
                    <input type="checkbox" onChange={() => changeRoles(TENANT_ADMIN)} checked={user.roles?.includes(TENANT_ADMIN)} className={`tenant-user-management-card-tenant-admin ${isSameUser && user.roles?.includes(TENANT_ADMIN) && !isGlobalAdmin? 'tenant-user-management-card-checkbox-value-disabled'
                        : 'tenant-user-management-card-checkbox-value-enabled'}`} disabled={isSameUser && userCancel.roles?.includes(TENANT_ADMIN) && !isGlobalAdmin}></input>
                    <div className={`tenant-user-management-card-tenant-admin tenant-user-management-card-checkbox-label ${isSameUser && userCancel.roles?.includes(TENANT_ADMIN) && !isGlobalAdmin ? 'tenant-user-management-card-checkbox-label-disabled' : ''}`} onClick={() => {if (!(isSameUser && userCancel.roles?.includes(TENANT_ADMIN) && !isGlobalAdmin)) {changeRoles(TENANT_ADMIN);}}}>
                        Tenant Admin
                    </div>
                </div>}
            {!editEnabled ?
               <div className="tenant-user-management-card-buttons">
              <Button
                className="tenant-user-management-card-left tenant-user-management-card-button" label="Delete" clickHandler={() => setShowConfirmDeletionModal(true)} disabled={isSameUser}/>
                <Button className="tenant-user-management-card-right tenant-user-management-card-button" label="Edit" clickHandler={toggleEdit}/>
             </div>
                :
                <div>
                    <div className="tenant-user-management-card-left tenant-user-management-card-button" onClick={toggleEdit}>
                        <div className="tenant-user-management-card-left tenant-user-management-card-button-label">
                            Cancel
                        </div>
                    </div>
                    <div className="tenant-user-management-card-right tenant-user-management-card-button" onClick={updateUserRoles}>
                        <div className="tenant-user-management-card-right tenant-user-management-card-button-label">
                            Save
                        </div>
                    </div>
                </div>}
        </div >
    </>
}

export default Card;